import { useState, useEffect } from 'react';
import {
  ensureConnected,
  subscribeBandUpdates,
  subscribeRealtimeState,
} from '../lib/realtime';
import { useProfileContext } from './useProfileContext';

/**
 * React binding for the real-time WebSocket. Call with an optional bandId
 * (defaults to the active profile). Returns:
 *   - refreshToken: increments every time a band-updated arrives for the active
 *     band, so data hooks can refetch in a useEffect keyed on it.
 *   - connected:    whether the socket is currently open (for a status pill).
 *
 * It also ensures the socket is connected while this hook is mounted and
 * disconnects on unmount when nothing else needs it (the socket is a shared
 * singleton, so unmounting one consumer does not kill it for others).
 */
export function useRealtime(bandId?: string) {
  const { activeProfileId } = useProfileContext();
  const effectiveId = bandId || activeProfileId;

  const [refreshToken, setRefreshToken] = useState(0);
  const [connected, setConnected] = useState(false);

  // Keep the singleton socket alive while this hook is mounted. The socket is
  // a shared singleton (the lib auto-reconnects), so unmounting one consumer
  // does not tear it down; explicit teardown on logout is handled by a bridge.
  useEffect(() => {
    ensureConnected();
    const unsubState = subscribeRealtimeState((s) => setConnected(s.connected));
    return () => unsubState();
  }, []);

  // Listen for band-updated events scoped to the active band.
  useEffect(() => {
    if (!effectiveId) return;
    const unsub = subscribeBandUpdates((ev) => {
      if (ev.bandId && String(ev.bandId) === String(effectiveId)) {
        setRefreshToken((n) => n + 1);
      }
    });
    return unsub;
  }, [effectiveId]);

  return { refreshToken, connected };
}
